import type { QueryClient } from '@tanstack/react-query';

/**
 * Canonical React Query key prefixes for booking-related queries.
 *
 * Every consumer hook (use-get-booking, use-get-booking-detail,
 * use-get-booking-stats, use-get-available-time-slots) MUST derive its key from
 * these helpers so that cache invalidation via `invalidateBookingCaches` hits
 * the right entries. The shared prefix `['booking']` (singular) is what the
 * invalidator prefix-matches against.
 */
export const bookingQueryKeys = {
  /** Singular prefix — all booking queries share this. Prefix-match only. */
  all: ['booking'] as const,
  list: (userId: string, filters?: Record<string, unknown>) =>
    [...bookingQueryKeys.all, 'get', userId, filters] as const,
  detail: (bookingId: string) =>
    [...bookingQueryKeys.all, 'detail', bookingId] as const,
  stats: (userId: string) =>
    [...bookingQueryKeys.all, 'stats', userId] as const,
  availableSlots: (cleanerId: string, date: string) =>
    [...bookingQueryKeys.all, 'slots', cleanerId, date] as const,
};

/**
 * Canonical React Query key prefixes for cleaner-side queries.
 *
 * Cleaner dashboard / booking-list / weekly-earnings / availability hooks
 * derive their keys from these helpers so that mutation `onSuccess`
 * invalidation via `invalidateCleanerCaches` hits the right entries.
 */
export const cleanerQueryKeys = {
  /** Prefix for all cleaner queries. Prefix-match only. */
  all: ['cleaner'] as const,
  bookings: (filters?: Record<string, unknown>) =>
    ['cleaner', 'bookings', filters] as const,
  bookingDetail: (bookingId: string) =>
    ['cleaner', 'booking', 'detail', bookingId] as const,
  dashboardPendingRequest: ['cleaner', 'dashboard', 'pending-request'] as const,
  weeklyEarnings: ['cleaner', 'weekly-earnings'] as const,
  dashboardAvailabilityWeek: [
    'cleaner',
    'availability',
    'dashboard-week',
  ] as const,
  /** Availability slots for a date range: ['cleaner','availability','range', from, to]. */
  availabilityDateRange: (from: string, to: string) =>
    ['cleaner', 'availability', 'range', from, to] as const,
  /** Availability slots for a single date: ['cleaner','availability','date', dateStr]. */
  availabilityDate: (dateStr: string) =>
    ['cleaner', 'availability', 'date', dateStr] as const,
  /** Dates-with-availability for a month: ['cleaner','availability','month', year, month]. */
  availabilityMonth: (year: number, month: number) =>
    ['cleaner', 'availability', 'month', year, month] as const,
  reviews: (cleanerId: string, page?: number) =>
    ['cleaner', 'reviews', cleanerId, { page }] as const,
};

interface InvalidateOptions {
  /** Accepted for call-site compatibility; prefix matching makes it unnecessary. */
  userId?: string;
  bookingId?: string;
  includeWallet?: boolean;
}

/**
 * Invalidates all booking caches and (optionally) wallet caches.
 *
 * Uses prefix matching (`exact: false`) so every consumer query keyed under
 * `['booking', …]` is covered — lists, detail, stats, and slots — regardless of
 * the trailing userId/filters/bookingId segments. This avoids the key-shape
 * drift that previously left list data stale for up to `staleTime` (5 min).
 */
export async function invalidateBookingCaches(
  queryClient: QueryClient,
  options: InvalidateOptions = {}
) {
  const { bookingId, includeWallet } = options;

  // Always invalidate the entire booking prefix — lists + detail + stats + slots.
  await queryClient.invalidateQueries({
    queryKey: bookingQueryKeys.all,
    exact: false,
  });

  if (bookingId) {
    await queryClient.invalidateQueries({
      queryKey: bookingQueryKeys.detail(bookingId),
      exact: false,
    });
  }

  if (includeWallet) {
    await queryClient.invalidateQueries({
      queryKey: ['wallet', 'balance'],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ['wallet', 'history'],
      exact: false,
    });
  }
}

/**
 * Invalidates all cleaner-side caches that depend on booking state.
 *
 * Call from cleaner mutation hooks (`accept`, `reject`, etc.) in their
 * `onSuccess` so any caller benefits — not just button components.
 * Uses prefix matching (`exact: false`) for broad coverage.
 */
export async function invalidateCleanerCaches(
  queryClient: QueryClient,
  options: { bookingId?: string } = {}
) {
  const { bookingId } = options;

  // Cleaner bookings list (infinite query).
  await queryClient.invalidateQueries({
    queryKey: cleanerQueryKeys.bookings(),
    exact: false,
  });

  // Dashboard pending-request banner.
  await queryClient.invalidateQueries({
    queryKey: cleanerQueryKeys.dashboardPendingRequest,
    exact: false,
  });

  // Dashboard weekly availability chart.
  await queryClient.invalidateQueries({
    queryKey: cleanerQueryKeys.dashboardAvailabilityWeek,
    exact: false,
  });

  // Weekly earnings chart.
  await queryClient.invalidateQueries({
    queryKey: cleanerQueryKeys.weeklyEarnings,
    exact: false,
  });

  if (bookingId) {
    await queryClient.invalidateQueries({
      queryKey: cleanerQueryKeys.bookingDetail(bookingId),
      exact: false,
    });
  }
}

/**
 * Determines polling interval for booking detail based on status.
 * - ongoing: 10 minutes
 * - requested/pending with no assigned cleaner or instant type: 15 seconds
 * - otherwise: no polling (false)
 */
export function getBookingDetailRefetchInterval(
  booking?: {
    status?: string;
    type_of_service?: string;
    cleaners?: unknown[];
  } | null
): number | false {
  if (!booking) return false;

  const status = booking.status?.toLowerCase();

  if (status === 'ongoing') {
    return 10 * 60 * 1000;
  }

  if (status === 'requested' || status === 'pending') {
    const isInstant =
      (booking.type_of_service ?? '').toLowerCase() === 'instant';
    const hasAssignedCleaner =
      Array.isArray(booking.cleaners) && booking.cleaners.length > 0;
    if (isInstant || !hasAssignedCleaner) {
      return 15 * 1000;
    }
  }

  return false;
}
